import * as React from "react"

export const Dialog = ({ open, onOpenChange, children }: {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children: React.ReactNode
}) => {
  const [isOpen, setIsOpen] = React.useState(open || false)

  React.useEffect(() => {
    if (open !== undefined) {
      setIsOpen(open)
    }
  }, [open])

  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen)
    onOpenChange?.(newOpen)
  }

  return (
    <>
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          if (child.type === DialogTrigger) {
            return React.cloneElement(
              child as React.ReactElement<{ onClick?: () => void }>,
              {
              onClick: () => handleOpenChange(true) 
              }
            )
          }
          if (child.type === DialogContent) {
            return isOpen ? (
              <div className="fixed inset-0 z-50 flex items-center justify-center">
                <div 
                  className="fixed inset-0 bg-black bg-opacity-50" 
                  onClick={() => handleOpenChange(false)}
                  onKeyDown={(e) => e.key === 'Escape' && handleOpenChange(false)}
                  role="button"
                  tabIndex={0}
                  aria-label="Close dialog"
                />
                <div className="relative z-10">
                  {React.cloneElement(
                    child as React.ReactElement<{ onClose?: () => void }>,
                    { onClose: () => handleOpenChange(false) }
                  )}
                </div>
              </div>
            ) : null
          }
        }
        return child
      })}
    </>
  )
}

export const DialogTrigger = ({ children, onClick, asChild, ...props }: {
  children: React.ReactNode
  onClick?: () => void
  asChild?: boolean
} & React.HTMLAttributes<HTMLDivElement>) => {
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(
      children as React.ReactElement<React.HTMLAttributes<HTMLElement>>,
      {
      onClick: (e: React.MouseEvent) => {
        e.preventDefault();
        if (onClick) onClick();
      },
      ...props
      }
    )
  }
  
  return (
    <div 
      onClick={onClick} 
      onKeyDown={(e) => e.key === 'Enter' && onClick && onClick()}
      className="cursor-pointer" 
      role="button"
      tabIndex={0}
      {...props}
    >
      {children}
    </div>
  )
}

export const DialogContent = ({ className, children, onClose, ...props }: {
  className?: string
  children: React.ReactNode
  onClose?: () => void
} & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={`bg-background p-6 rounded-lg border shadow-lg max-w-lg w-full mx-4 ${className || ''}`}
    {...props}
  >
    <button 
      onClick={onClose}
      className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
    >
      ×
    </button>
    {children}
  </div>
)

export const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={`flex flex-col space-y-1.5 text-center sm:text-left ${className || ''}`}
    {...props}
  />
)

export const DialogTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, children, ...props }, ref) => 
  children ? (
    <h2
      ref={ref}
      className={`text-lg font-semibold leading-none tracking-tight ${className || ''}`}
      {...props}
    >
      {children}
    </h2>
  ) : null
)
DialogTitle.displayName = "DialogTitle"

export const DialogDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={`text-sm text-muted-foreground ${className || ''}`}
    {...props}
  />
))
DialogDescription.displayName = "DialogDescription" 